/**
 * Bundled network data under public/data/loader, the offline fallback when D1
 * is unreachable (and the only source in demo mode). File mapping per network
 * follows the previous app; unknown networks use the demo files.
 */
import { getJson } from '@/lib/api';
import type {
  Antenna,
  EphemerisFile,
  Mission,
  MissionTimeline,
  NetworkDataset,
  NetworkId,
  PriorityTier,
  ScenarioPreset,
  TdrsSatellite,
} from '../types';

const DATA_BASE = '/data/loader';

interface StaticFiles {
  missions: string | null;
  antennas: string;
  tiers: string;
}

const FILES: Record<string, StaticFiles> = {
  demo: { missions: 'missions.json', antennas: 'antennas.json', tiers: 'priority-tiers.json' },
  nasa: { missions: 'missions-nasa.json', antennas: 'antennas-nasa.json', tiers: 'priority-tiers.json' },
  spaceforce: {
    missions: 'missions-spaceforce.json',
    antennas: 'antennas-spaceforce.json',
    tiers: 'priority-tiers-sf.json',
  },
  noaa: { missions: 'missions-noaa.json', antennas: 'antennas-noaa.json', tiers: 'priority-tiers.json' },
  noaa_extended: { missions: 'missions-noaa-extended.json', antennas: 'antennas-noaa.json', tiers: 'priority-tiers.json' },
  artemis: {
    missions: 'missions-artemis.json',
    antennas: 'antennas-artemis.json',
    tiers: 'priority-tiers.json',
  },
  mars: { missions: 'missions-mars.json', antennas: 'antennas-mars.json', tiers: 'priority-tiers.json' },
  starlink: { missions: null, antennas: 'antennas-starlink.json', tiers: 'priority-tiers.json' },
};

/** Antenna files for the live constellations (missions come from TLE feeds). */
export const LIVE_ANTENNA_FILES: Record<string, string> = {
  oneweb: 'antennas-oneweb.json',
  stations: 'antennas-stations.json',
  gnss: 'antennas-igs.json',
  sentinel: 'antennas-sentinel.json',
  aws_ground_station: 'antennas-aws-ground-station.json',
};

export function antennaFileForNetwork(network: string): string | null {
  return (
    FILES[network]?.antennas ??
    LIVE_ANTENNA_FILES[network] ??
    (network === 'satnogs' ? 'antennas.json' : null)
  );
}

export function missionFileForNetwork(network: string): string | null {
  return FILES[network]?.missions ?? null;
}

export function staticFile<T>(name: string): Promise<T> {
  return getJson<T>(`${DATA_BASE}/${name}`);
}

export const loadStaticAntennas = (file: string) =>
  staticFile<{ antennas: Antenna[] }>(file).then((r) => r.antennas);
export const loadStaticMissions = (file: string) =>
  staticFile<{ missions: Mission[] }>(file).then((r) => r.missions);
export const loadStaticTiers = (file: string) =>
  staticFile<{ tiers: PriorityTier[] }>(file).then((r) => r.tiers);
export const loadStaticPresets = () =>
  staticFile<{ scenarios: ScenarioPreset[] }>('scenarios.json').then((r) => r.scenarios);
export const loadStaticTdrss = () =>
  staticFile<{ tdrss: TdrsSatellite[] }>('tdrss-constellation.json').then((r) => r.tdrss);
export const loadMoonEphemeris = () => staticFile<EphemerisFile>('moon-ephemeris.json');
export const loadArtemisEphemeris = () => staticFile<EphemerisFile>('artemis2-ephemeris.json');
export const loadArtemisTimeline = () => staticFile<MissionTimeline>('artemis2-timeline.json');
export const loadMarsEphemeris = () => staticFile<EphemerisFile>('mars-planet-ephemeris.json');

/** Default analysis and view toggles when D1 toggles are unavailable. */
export function defaultToggles(network: NetworkId): NetworkDataset['toggles'] {
  return [
    { id: 'tdrss_relay', name: 'TDRSS Relay Modeling', enabled: !['noaa', 'noaa_extended'].includes(network), category: 'analysis' },
    { id: 'weather_overlays', name: 'Weather Overlays', enabled: true, category: 'visualization' },
  ];
}

/** Optional lookups that are not fatal when missing: an empty list is the documented fallback. */
async function optional<T>(load: () => Promise<T[]>, label: string): Promise<T[]> {
  try {
    return await load();
  } catch (err) {
    console.warn(`[loader] optional static data ${label} unavailable:`, err);
    return [];
  }
}

export async function loadStaticDataset(network: NetworkId): Promise<NetworkDataset> {
  const files = FILES[network] ?? FILES.demo;
  const [missions, antennas, tiers, presets, tdrss] = await Promise.all([
    files.missions ? loadStaticMissions(files.missions) : Promise.resolve([] as Mission[]),
    loadStaticAntennas(files.antennas),
    loadStaticTiers(files.tiers),
    optional(loadStaticPresets, 'scenarios.json'),
    optional(loadStaticTdrss, 'tdrss-constellation.json'),
  ]);
  return {
    network,
    missions,
    antennas,
    tiers,
    toggles: defaultToggles(network),
    tdrss,
    scenarios: [],
    presets,
    source: 'static',
  };
}
